import React, { useState, useEffect, useRef, ReactElement } from "react";
import { Link } from "react-router-dom";
import AppIcon from "ui/components/icons/AppIcon";
import {
  iconCalendarLines,
  iconCommentOutlined,
  iconToday,
  iconPlus,
} from "ui/components/icons";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";

const constructActionsWithLinks = (
  icon: ReactElement,
  label: string,
  href: string,
) => ({
  icon: (
    <Link to={href} style={{ display: "flex", alignItems: "center" }}>
      {icon}
    </Link>
  ),
  name: label,
  href,
});

const actions = [
  {
    icon: (
      <AppIcon icon={iconToday} fontSize="large" sx={{ color: "orange" }} />
    ),
    name: "New Meeting",
    href: "/meeting/new",
  },
  {
    icon: (
      <AppIcon
        icon={iconCommentOutlined}
        fontSize="large"
        sx={{ color: "green" }}
      />
    ),
    name: "New Public Investment Comment",
    href: "/comment/new?type=Public",
  },
  {
    icon: (
      <AppIcon
        icon={iconCommentOutlined}
        fontSize="large"
        sx={{ color: "orange" }}
      />
    ),
    name: "New Private Investment Comment",
    href: "/comment/new?type=Private",
  },
  {
    icon: (
      <AppIcon
        icon={iconCalendarLines}
        fontSize="large"
        sx={{ color: "common.caGridBadge.main" }}
      />
    ),
    name: "New Annual Meeting Note",
    href: "/annual-meeting-note/new",
  },
];

const actionsWithLinks = actions.map((action) =>
  constructActionsWithLinks(action.icon, action.name, action.href),
);

const CreationSpeedDial = () => {
  const [open, setOpen] = useState(false);
  const hasFooter = ["/comment/", "/meeting/", "/annual-meeting-note/"].some(
    (path) => location.pathname.includes(path),
  );

  const speedDialRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      speedDialRef.current &&
      !speedDialRef.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [open]);

  return (
    <SpeedDial
      ref={speedDialRef}
      color="primary"
      ariaLabel="Creation SpeedDial"
      sx={(theme) => ({
        position: "fixed",
        bottom: theme.spacing(hasFooter ? 10 : 2),
        right: theme.spacing(2),
        transition: "all .3s ease-in-out",
        "@media print": {
          display: "none",
        },
      })}
      icon={<AppIcon icon={iconPlus} fontSize="small" />}
      open={open}
      onClick={() => setOpen(!open)}
    >
      {actionsWithLinks.map((action) => (
        <SpeedDialAction
          key={action.name}
          icon={action.icon}
          tooltipTitle={action.name}
        />
      ))}
    </SpeedDial>
  );
};

export default CreationSpeedDial;
